import React from 'react';
import { TicketStatus, statusesForCategory } from '@/types/domain';

/**
 * V4.9.13 — Chevron "stage pipeline" for the ticket header.
 *
 * Renders the ticket's category status palette (statusesForCategory) as a
 * row of connected chevron arrows — like a sales-pipeline / deal-stage bar.
 * The CURRENT stage is highlighted in a vivid colour; earlier stages read as
 * "done" (filled brand blue), later stages as "upcoming" (muted grey).
 * Terminal stages get semantic colour — green for a successful close, slate
 * for Cancelled.
 *
 * V4.9.13a — Below the chevrons, a MANDATORY, always-visible "Next action"
 * line names the exact person whose turn it is (e.g. "Lavanya · Solver"),
 * with a tone-coloured dot. This never truncates, unlike the in-chevron
 * label, so the responsible person is always crystal clear.
 */

interface AwaitedBy {
  role:
    | 'solver'
    | 'approver'
    | 'communicator'
    | 'owner'
    | 'customer'
    | 'design'
    | 'vendor'
    | 'done'
    | 'cancelled'
    | string;
  name: string;
  tone?: string;
}

interface Props {
  category: string;
  status: TicketStatus | string;
  /** Who the ticket is waiting on right now — shown in the Next action line. */
  awaited?: AwaitedBy | null;
}

/** Verbose status → short pipeline label (keeps the chevrons clean). */
const SHORT_LABEL: Partial<Record<string, string>> = {
  [TicketStatus.InvestigationInProgress]: 'Investigating',
  [TicketStatus.InvestigationAwaitingApproval]: 'Awaiting Admin Approval',
  [TicketStatus.InvestigationAwaitingCx]: 'Awaiting CX',
  [TicketStatus.ApprovedForReplacement]: 'Approved',
  [TicketStatus.ApprovedReprint]: 'Approved',
  [TicketStatus.ApprovedReprintInProgress]: 'Reprinting',
  [TicketStatus.ApprovedRefundCompleted]: 'Refund done',
  [TicketStatus.ReprintInProduction]: 'In production',
  [TicketStatus.ReplacementInProduction]: 'In production',
  [TicketStatus.ReprintShippedInformCustomer]: 'Shipped',
  [TicketStatus.ReplacementShippedInformCustomer]: 'Shipped',
  [TicketStatus.CallCarrierAndInformCustomer]: 'Call carrier',
  [TicketStatus.AwaitingCustomerResponse]: 'Awaiting customer',
  [TicketStatus.AwaitingCustomerFeedback]: 'Awaiting customer',
  [TicketStatus.AwaitingReturnDelivery]: 'Awaiting return',
  [TicketStatus.DeliveredCompleted]: 'Delivered',
  [TicketStatus.CompletedRefunded]: 'Refunded',
  [TicketStatus.RefundProcessed]: 'Refund processed',
  [TicketStatus.InformCustomer]: 'Inform customer',
  [TicketStatus.ReprintCompleted]: 'Completed',
  [TicketStatus.ReplacementCompleted]: 'Completed',
  [TicketStatus.AwaitingCustomerReply]: 'Awaiting customer',
  [TicketStatus.InquiryReceived]: 'Inquiry'
};

/** Stages that mean the ticket closed SUCCESSFULLY (current → green). */
const SUCCESS_TERMINALS = new Set<string>([
  TicketStatus.Completed,
  TicketStatus.CompletedRefunded,
  TicketStatus.ReprintCompleted,
  TicketStatus.ReplacementCompleted,
  TicketStatus.ApprovedRefundCompleted,
  TicketStatus.DeliveredCompleted,
  TicketStatus.RefundProcessed,
  TicketStatus.ResolvedCustomerAccepted
]);

const PERSON_ROLES = new Set(['solver', 'approver', 'communicator', 'owner']);

function roleLabel(role: string): string {
  switch (role) {
    case 'solver':
      return 'Solver';
    case 'approver':
      return 'Approver';
    case 'communicator':
      return 'Communicator';
    case 'owner':
      return 'Owner';
    default:
      return '';
  }
}

/**
 * Mandatory "whose turn is it" line. Always rendered when we know who's on
 * the hook. The person's NAME is the hero (bold); their role + a tone dot
 * give context. For external waits / closed / cancelled it shows the state.
 */
function NextAction({ awaited }: { awaited?: AwaitedBy | null }) {
  if (!awaited || !awaited.name) return null;
  const isPerson = PERSON_ROLES.has(awaited.role);
  const tone =
    awaited.tone || (awaited.role === 'done' ? 'green' : awaited.role === 'cancelled' ? 'grey' : 'amber');
  return (
    <div className={`pipeline-next tone-${tone}`}>
      <span className="pipeline-next-label">{isPerson ? 'Next action' : 'Status'}</span>
      <span className="pipeline-next-dot" aria-hidden />
      <span className="pipeline-next-name">{awaited.name}</span>
      {isPerson ? <span className="pipeline-next-role">· {roleLabel(awaited.role)}</span> : null}
    </div>
  );
}

export function StatusPipeline({ category, status, awaited }: Props) {
  const palette = statusesForCategory(category) as string[];
  let currentIndex = palette.indexOf(status as string);
  // Legacy / off-palette status (rare) — show it as a single current chevron
  // rather than rendering nothing.
  const stages = currentIndex === -1 ? [status as string] : palette;
  if (currentIndex === -1) currentIndex = 0;

  return (
    <div className="status-pipeline-wrap">
      <div className="status-pipeline" role="list" aria-label="Ticket progress">
        {stages.map((s, i) => {
          const state = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming';
          const isCancelled = s === TicketStatus.Cancelled;
          const isSuccess = SUCCESS_TERMINALS.has(s);
          const cls = [
            'pipeline-step',
            `is-${state}`,
            i === 0 ? 'is-first' : '',
            i === stages.length - 1 ? 'is-last' : '',
            state === 'current' && isCancelled ? 'is-terminal-cancel' : '',
            state === 'current' && isSuccess ? 'is-terminal-success' : ''
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div key={`${s}-${i}`} className={cls} role="listitem" title={s} aria-current={state === 'current'}>
              <span className="pipeline-step-label">{SHORT_LABEL[s] ?? s}</span>
            </div>
          );
        })}
      </div>
      <NextAction awaited={awaited} />
    </div>
  );
}
